import { Link } from 'react-router-dom';
import { BookOpen, ChevronLeft, GraduationCap, Package } from 'lucide-react';
import {
  EmptyState,
  ErrorState,
  Progress,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import {
  isCourseDone,
  dueState,
  progressLabel,
  useMyLearning,
  type AssignedCourse,
  type AssignedQuiz,
} from './api';

/**
 * הלומדות של העובד (PRD 5.6.3).
 *
 * מה שחייב קודם: הסדר הוא לפי חובה ואז לפי דדליין, והוא נקבע בשרת.
 * המסך לא ממיין מחדש, כדי שלא יהיו שני סדרים שונים לאותו דבר.
 */
export function LearningPage() {
  const learning = useMyLearning();

  if (learning.isPending) return <Skeleton className="m-4 h-40" />;
  if (learning.isError) return <ErrorState onRetry={() => void learning.refetch()} />;

  const { courses, quizzes } = learning.data ?? { courses: [], quizzes: [] };
  const open = courses.filter((course) => !isCourseDone(course));
  const done = courses.filter(isCourseDone);

  return (
    <div className="grid content-start gap-4">
      <SubBar title="לומדות" subtitle="מה שמחכה לך ללמוד" />

      <div className="grid gap-6 px-4 pb-8">
        {/* ארכיון המוצרים יושב באותו מודול, ואין לו מקום בסרגל התחתון */}
        <Link
          to="/products"
          className="flex items-center gap-2 rounded-md border border-line bg-raised p-3 text-body font-medium text-ink no-underline"
        >
          <Package size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">המוצרים שלנו</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>

        {courses.length === 0 && quizzes.length === 0 && (
          <EmptyState
            title="אין לומדות פתוחות"
            description="כשההנהלה תשייך לך לומדה היא תופיע כאן, ותקבל/י התראה."
          />
        )}

        {open.length > 0 && (
          <Section title="ללמוד" count={open.length}>
            {open.map((course) => (
              <CourseRow key={course.course_id} course={course} />
            ))}
          </Section>
        )}

        {quizzes.length > 0 && (
          <Section title="חידונים" count={quizzes.length}>
            {quizzes.map((quiz) => (
              <QuizRow key={quiz.quiz_id} quiz={quiz} />
            ))}
          </Section>
        )}

        {done.length > 0 && (
          <Section title="סיימת" count={done.length}>
            {done.map((course) => (
              <CourseRow key={course.course_id} course={course} />
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <h2 className="m-0 font-display text-display-m font-semibold">
        {title} <span className="text-body-sm font-normal text-muted">({count})</span>
      </h2>
      {children}
    </section>
  );
}

function CourseRow({ course }: { course: AssignedCourse }) {
  const due = dueState(course.due_date);
  const finished = isCourseDone(course);
  const lessonsDone = course.lesson_count > 0 && course.completed_count >= course.lesson_count;
  const started = course.completed_count > 0;

  // את מה שנשאר לעשות אומרים במילים, לא רק בסרגל
  const action = finished
    ? null
    : lessonsDone
      ? 'נשאר החידון'
      : started
        ? 'להמשיך מאיפה שעצרת'
        : 'להתחיל';

  return (
    <Link
      to={`/learning/${course.course_id}`}
      className="grid gap-2 rounded-md border border-line bg-raised p-3 no-underline"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex items-center gap-2 text-title font-medium text-ink">
          <BookOpen size={20} strokeWidth={1.75} aria-hidden="true" />
          {course.title}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {course.is_mandatory && !finished && <StatusBadge tone="warn">חובה</StatusBadge>}
          {finished && <StatusBadge tone="good">הושלמה</StatusBadge>}
          {/* ChevronLeft ולא Right: ב RTL "הלאה" הוא שמאלה */}
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </div>
      </div>

      {course.description && <p className="m-0 text-body-sm text-muted">{course.description}</p>}

      <Progress
        value={course.completed_count}
        max={course.lesson_count}
        label={progressLabel(course.completed_count, course.lesson_count)}
      />

      <div className="flex flex-wrap items-center gap-2">
        {!finished && due && (
          <span
            className={
              due.tone === 'bad'
                ? 'text-body-sm text-bad'
                : due.tone === 'warn'
                  ? 'text-body-sm text-warn'
                  : 'text-body-sm text-muted'
            }
          >
            {due.label}
          </span>
        )}
        {course.category && <span className="text-label text-muted">{course.category}</span>}
      </div>

      {action && <span className="text-body-sm font-medium text-ink">{action}</span>}
    </Link>
  );
}

function QuizRow({ quiz }: { quiz: AssignedQuiz }) {
  const due = dueState(quiz.due_date);

  return (
    <Link
      to={`/learning/quiz/${quiz.quiz_id}`}
      className="grid gap-2 rounded-md border border-line bg-raised p-3 no-underline"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex items-center gap-2 text-title font-medium text-ink">
          <GraduationCap size={20} strokeWidth={1.75} aria-hidden="true" />
          {quiz.title}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {quiz.passed ? (
            <StatusBadge tone="good">עברת</StatusBadge>
          ) : (
            quiz.is_mandatory && <StatusBadge tone="warn">חובה</StatusBadge>
          )}
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </div>
      </div>

      <p className="m-0 text-body-sm text-muted">
        {quiz.best_score === null
          ? `ציון עובר ${quiz.pass_score}`
          : `הציון הטוב שלך ${quiz.best_score} מתוך 100, ציון עובר ${quiz.pass_score}`}
        {!quiz.passed && due && ` · ${due.label}`}
      </p>

      <span className="text-body-sm font-medium text-ink">
        {quiz.passed ? 'לנסות שוב' : quiz.best_score === null ? 'להתחיל את החידון' : 'לנסות שוב'}
      </span>
    </Link>
  );
}
